import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button, Card, Col, Divider, message, Row, Statistic, Tag} from "antd";
import {
    BarChartOutlined,
    FallOutlined,
    FilePdfOutlined,
    LeftOutlined,
    RightOutlined,
    RiseOutlined
} from '@ant-design/icons';
import moment from "moment";
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {instance} from "../../API/API";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Компонент дашборда аналитики доходов
 * Отображает сводку, сравнения и графики доходов
 */
const IncomeDashboard = () => {

    const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1);
    const [selectedYear, setSelectedYear] = useState(moment().year());
    const [exporting, setExporting] = useState(false);

    const [income, setIncome] = useState(null)
    const [payments, setPayments] = useState(null)
    const [one_income, setOneIncome] = useState(null)

    const [loading, setLoading] = useState(false)

    const dashboardRef = useRef();

    useEffect(() => {
        const getInitialData = async () => {
            await getIncome()
            await getOneIncome()
            await getPayments()
        }

        const getIncome = async () => {
            const income_res = await instance.get('finance/getIncome.php?api-key=null')
            if (income_res && income_res.data) {
                setIncome(income_res.data.income);
            }
        }
        const getOneIncome = async () => {
            const one_income_res = await instance.get('finance/getOneIncome.php?api-key=null')
            if (one_income_res && one_income_res.data) {
                setOneIncome(one_income_res.data.one_income);
            }
        }
        const getPayments = async () => {
            const payments_res = await instance.get('finance/getPayment.php?api-key=null')
            if (payments_res && payments_res.data) {
                setPayments(payments_res.data.payments);
            }
        }

        try {
            setLoading(true)
            getInitialData()
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }, [])

    /**
     * Функция для экспорта дашборда в PDF
     */
    const exportToPDF = async () => {
        if (!dashboardRef.current) return;

        setExporting(true);
        try {
            // Создаем canvas из содержимого дашборда
            const canvas = await html2canvas(dashboardRef.current, {
                scale: 2, // Увеличиваем качество
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            // Создаем PDF
            const pdf = new jsPDF('landscape', 'mm', 'a3');
            const imgData = canvas.toDataURL('image/png');

            // Получаем размеры страницы
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Рассчитываем размеры изображения для заполнения страницы
            const imgWidth = pageWidth - 40; // Отступы по 10мм с каждой стороны
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Добавляем заголовок
            pdf.setFontSize(16);
            pdf.setTextColor(40, 40, 40);
            pdf.text('Incomes analytics', 15, 15);

            pdf.setFontSize(10);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`Generated: ${moment().format('DD.MM.YYYY HH:mm')}`, pageWidth - 15, 15, {align: 'right'});

            // Добавляем основное изображение
            pdf.addImage(imgData, 'PNG', 10, 25, imgWidth, imgHeight);

            // Сохраняем PDF
            pdf.save(`аналитика-доходов-${moment().format('YYYY-MM-DD')}.pdf`);

            message.success('PDF успешно сгенерирован');
        } catch (error) {
            console.error('Ошибка при генерации PDF:', error);
            message.error('Ошибка при генерации PDF');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Основная функция анализа данных о доходах
     */
    const incomeAnalysis = useMemo(() => {
        if (!income || !payments || !one_income) return null;

        // Вспомогательные функции
        const parseCostString = (costStr) => {
            return costStr.split('`').map(cost =>
                parseFloat(cost.replace(',', '.')) || 0
            );
        };

        const getMonthCost = (incomeItem, month) => {
            const costs = parseCostString(incomeItem.cost);
            return costs[month - 1] || 0;
        };

        // Расчеты по периодическим доходам (планируемые)
        const calculatePeriodicIncome = (year, month) => {
            let total = 0;
            let activeObjects = 0;

            income.forEach(item => {
                if (item.enable === "1") {
                    activeObjects++;
                    total += getMonthCost(item, month);
                }
            });

            return {total, activeObjects};
        };

        // Расчеты по фактическим платежам (периодические доходы)
        const calculateActualPayments = (year, month) => {
            const monthlyPayments = payments.filter(payment => {
                const paymentDate = moment(payment.date * 1000);
                return paymentDate.year() === year && paymentDate.month() + 1 === month;
            });

            const total = monthlyPayments.reduce((sum, payment) =>
                sum + (parseFloat(payment.cost) || 0), 0
            );

            return {total, count: monthlyPayments.length};
        };

        // Расчеты по разовым доходам
        const calculateOneTimeIncome = (year, month) => {
            const monthlyOneTime = one_income.filter(income => {
                const incomeDate = moment(income.date * 1000);
                return incomeDate.year() === year && incomeDate.month() + 1 === month;
            });

            const total = monthlyOneTime.reduce((sum, item) =>
                sum + (parseFloat(item.cost) || 0), 0
            );

            return {total, count: monthlyOneTime.length};
        };

        // ОПРЕДЕЛЯЕМ ГОДЫ ДЛЯ СРАВНЕНИЯ
        const currentYear = selectedYear; // Выбранный пользователем год
        const previousYear = selectedYear - 1; // Предыдущий год для сравнения

        // ОПРЕДЕЛЯЕМ ДИАПАЗОН МЕСЯЦЕВ ДЛЯ ОТОБРАЖЕНИЯ
        // Если выбран текущий год и месяц в будущем недоступен, ограничиваем текущим месяцем
        const isCurrentYearAndFuture = selectedYear === moment().year() && selectedMonth > moment().month() + 1;
        const maxDisplayMonth = isCurrentYearAndFuture ? moment().month() + 1 : selectedMonth;

        // Данные за выбранный год (с января по выбранный месяц)
        const currentYearStats = [];
        for (let month = 1; month <= maxDisplayMonth; month++) {
            const periodicPlanned = calculatePeriodicIncome(currentYear, month);
            const periodicActual = calculateActualPayments(currentYear, month);
            const oneTimeActual = calculateOneTimeIncome(currentYear, month);

            currentYearStats.push({
                month,
                monthName: moment().month(month - 1).format('MMM'),
                planned: periodicPlanned.total,
                periodicActual: periodicActual.total,
                oneTimeActual: oneTimeActual.total,
                totalActual: periodicActual.total + oneTimeActual.total,
                paymentsCount: periodicActual.count + oneTimeActual.count
            });
        }

        // Данные за предыдущий год (аналогичный период - с января по выбранный месяц)
        const previousYearStats = [];
        for (let month = 1; month <= maxDisplayMonth; month++) {
            const periodicPlanned = calculatePeriodicIncome(previousYear, month);
            const periodicActual = calculateActualPayments(previousYear, month);
            const oneTimeActual = calculateOneTimeIncome(previousYear, month);

            previousYearStats.push({
                month,
                monthName: moment().month(month - 1).format('MMM'),
                planned: periodicPlanned.total,
                periodicActual: periodicActual.total,
                oneTimeActual: oneTimeActual.total,
                totalActual: periodicActual.total + oneTimeActual.total,
                paymentsCount: periodicActual.count + oneTimeActual.count
            });
        }

        // Подготовка данных для графика
        const chartData = currentYearStats.map((currentMonthData, index) => {
            const previousYearMonthData = previousYearStats[index] || {
                periodicActual: 0,
                oneTimeActual: 0
            };

            return {
                month: currentMonthData.monthName,
                currentPeriodic: currentMonthData.periodicActual,
                previousYearPeriodic: previousYearMonthData.periodicActual,
                currentOneTime: currentMonthData.oneTimeActual,
                previousYearOneTime: previousYearMonthData.oneTimeActual,
                currentPlanned: currentMonthData.planned,
                totalCurrent: currentMonthData.periodicActual + currentMonthData.oneTimeActual,
                totalPreviousYear: previousYearMonthData.periodicActual + previousYearMonthData.oneTimeActual
            };
        });

        // Расчет показателей для ВЫБРАННОГО месяца
        const selectedPeriodicPlanned = calculatePeriodicIncome(selectedYear, selectedMonth);
        const selectedPeriodicActual = calculateActualPayments(selectedYear, selectedMonth);
        const selectedOneTimeActual = calculateOneTimeIncome(selectedYear, selectedMonth);

        // Расчет показателей за аналогичный месяц ПРЕДЫДУЩЕГО года
        const sameMonthPreviousYearPeriodicPlanned = calculatePeriodicIncome(selectedYear - 1, selectedMonth);
        const sameMonthPreviousYearPeriodicActual = calculateActualPayments(selectedYear - 1, selectedMonth);
        const sameMonthPreviousYearOneTimeActual = calculateOneTimeIncome(selectedYear - 1, selectedMonth);

        // Суммы за период с начала года до ВЫБРАННОГО месяца
        const selectedYearToDate = {
            periodicPlanned: currentYearStats
                .reduce((sum, month) => sum + month.planned, 0),
            periodicActual: currentYearStats
                .reduce((sum, month) => sum + month.periodicActual, 0),
            oneTimeActual: currentYearStats
                .reduce((sum, month) => sum + month.oneTimeActual, 0),
            totalActual: currentYearStats
                .reduce((sum, month) => sum + month.totalActual, 0)
        };

        const previousYearToDate = {
            periodicPlanned: previousYearStats
                .reduce((sum, month) => sum + month.planned, 0),
            periodicActual: previousYearStats
                .reduce((sum, month) => sum + month.periodicActual, 0),
            oneTimeActual: previousYearStats
                .reduce((sum, month) => sum + month.oneTimeActual, 0),
            totalActual: previousYearStats
                .reduce((sum, month) => sum + month.totalActual, 0)
        };

        // Проценты изменений
        const sameMonthYearlyChangePercent = (sameMonthPreviousYearPeriodicActual.total + sameMonthPreviousYearOneTimeActual.total) > 0 ?
            ((selectedPeriodicActual.total + selectedOneTimeActual.total) - (sameMonthPreviousYearPeriodicActual.total + sameMonthPreviousYearOneTimeActual.total)) /
            (sameMonthPreviousYearPeriodicActual.total + sameMonthPreviousYearOneTimeActual.total) * 100 : 0;

        const yearlyChangePercent = previousYearToDate.totalActual > 0 ?
            ((selectedYearToDate.totalActual - previousYearToDate.totalActual) / previousYearToDate.totalActual * 100) : 0;

        return {
            selected: {
                planned: selectedPeriodicPlanned.total,
                actual: selectedPeriodicActual.total + selectedOneTimeActual.total,
                actualPeriodic: selectedPeriodicActual.total,
                periodic: selectedPeriodicPlanned.total,
                oneTime: selectedOneTimeActual.total,
                activeObjects: selectedPeriodicPlanned.activeObjects,
                paymentsCount: selectedPeriodicActual.count + selectedOneTimeActual.count
            },
            selectedYearToDate,
            previousYearToDate,
            changes: {
                sameMonthYearly: {
                    value: (selectedPeriodicActual.total + selectedOneTimeActual.total) - (sameMonthPreviousYearPeriodicActual.total + sameMonthPreviousYearOneTimeActual.total),
                    percent: sameMonthYearlyChangePercent,
                    isPositive: sameMonthYearlyChangePercent >= 0
                },
                yearly: {
                    value: selectedYearToDate.totalActual - previousYearToDate.totalActual,
                    percent: yearlyChangePercent,
                    isPositive: yearlyChangePercent >= 0
                }
            },
            chartData,
            currentMonth: selectedMonth,
            selectedMonth,
            selectedYear,
            sameMonthPreviousYear: {
                actual: sameMonthPreviousYearPeriodicActual.total + sameMonthPreviousYearOneTimeActual.total,
                periodicActual: sameMonthPreviousYearPeriodicActual.total,
                oneTimeActual: sameMonthPreviousYearOneTimeActual.total,
                periodicPlanned: sameMonthPreviousYearPeriodicPlanned.total
            },
            displayMonthsCount: maxDisplayMonth,
            comparisonYear: previousYear
        };
    }, [income, payments, one_income, selectedMonth, selectedYear]);

    // Функции для переключения месяцев
    const handlePreviousMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedMonth(selectedMonth - 1);
        }
    };

    const handleNextMonth = () => {
        const today = moment();
        const isFutureDate = selectedYear > today.year() ||
            (selectedYear === today.year() && selectedMonth > today.month() + 1);

        if (isFutureDate) return;

        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(selectedMonth + 1);
        }
    };

    // Функция для получения названия месяца
    const getMonthName = (monthNumber) => {
        return moment().month(monthNumber - 1).format('MMMM');
    };

    if (loading) {
        return <div>Загрузка данных...</div>;
    }
    if (!incomeAnalysis) {
        return <div>Данные не загружены.</div>;
    }
    const {
        selected,
        changes,
        chartData,
        selectedYearToDate,
        previousYearToDate,
        sameMonthPreviousYear,
        comparisonYear,
        displayMonthsCount
    } = incomeAnalysis;

    // Конфигурация графика
    const renderChart = () => {
        return (
            <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{top: 20, right: 30, left: 20, bottom: 5}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis
                        dataKey="month"
                        stroke="#666"
                        fontSize={12}
                    />
                    <YAxis
                        stroke="#666"
                        fontSize={12}
                        tickFormatter={(value) => `${value} €`}
                    />
                    <Tooltip
                        formatter={(value, name) => [
                            `${parseFloat(value).toFixed(2)} €`,
                            name
                        ]}
                        labelFormatter={(label) => `Месяц: ${label}`}
                    />
                    <Legend/>

                    <Line
                        type="monotone"
                        dataKey="currentPeriodic"
                        name={`Периодические ${selectedYear} (факт)`}
                        stroke="#1890ff"
                        strokeWidth={3}
                        dot={{fill: '#1890ff', strokeWidth: 2, r: 4}}
                        activeDot={{r: 6}}
                    />

                    <Line
                        type="monotone"
                        dataKey="previousYearPeriodic"
                        name={`Периодические ${comparisonYear} (факт)`}
                        stroke="#69c0ff"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{fill: '#69c0ff', strokeWidth: 2, r: 4}}
                    />

                    <Line
                        type="monotone"
                        dataKey="currentOneTime"
                        name={`Разовые ${selectedYear} (факт)`}
                        stroke="#52c41a"
                        strokeWidth={3}
                        dot={{fill: '#52c41a', strokeWidth: 2, r: 4}}
                        activeDot={{r: 6}}
                    />

                    <Line
                        type="monotone"
                        dataKey="previousYearOneTime"
                        name={`Разовые ${comparisonYear} (факт)`}
                        stroke="#95de64"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{fill: '#95de64', strokeWidth: 2, r: 4}}
                    />
                </LineChart>
            </ResponsiveContainer>
        );
    };

    return (
        <div ref={dashboardRef}>
            <Card
                title={
                    <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        <BarChartOutlined style={{color: '#1890ff', fontSize: '20px'}}/>
                        <span>Аналитика доходов</span>
                    </div>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<FilePdfOutlined/>}
                        onClick={exportToPDF}
                        loading={exporting}
                        style={{background: '#ff4d4f', borderColor: '#ff4d4f'}}
                    >
                        Экспорт в PDF
                    </Button>
                }
                className="income-dashboard"
            >
                {/* Ключевые метрики */}
                <Row gutter={[16, 16]} style={{marginBottom: 24}}>
                    <Col span={12}>
                        <Card
                            size="small"
                            title={
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <span>Доход за месяц</span>
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                        <Button
                                            size="small"
                                            icon={<LeftOutlined/>}
                                            onClick={handlePreviousMonth}
                                        />
                                        <span style={{fontWeight: 'bold', minWidth: '120px', textAlign: 'center'}}>
                                            {getMonthName(selectedMonth)} {selectedYear}
                                        </span>
                                        <Button
                                            size="small"
                                            icon={<RightOutlined/>}
                                            onClick={handleNextMonth}
                                            disabled={
                                                selectedYear > moment().year() ||
                                                (selectedYear === moment().year() && selectedMonth >= moment().month() + 1)
                                            }
                                        />
                                    </div>
                                </div>
                            }
                        >
                            <Statistic
                                title={`Фактический доход (${getMonthName(selectedMonth)} ${selectedYear})`}
                                value={selected.actual}
                                precision={2}
                                valueStyle={{color: '#52c41a'}}
                                prefix="€"
                                suffix={
                                    <Tag color={changes.sameMonthYearly.isPositive ? 'success' : 'error'}>
                                        {changes.sameMonthYearly.isPositive ? <RiseOutlined/> : <FallOutlined/>}
                                        {Math.abs(changes.sameMonthYearly.percent).toFixed(1)}%
                                    </Tag>
                                }
                            />
                            <div style={{fontSize: 12, color: '#666', marginTop: 8}}>
                                vs {getMonthName(selectedMonth)} {selectedYear - 1}: {changes.sameMonthYearly.value.toFixed(2)}€
                            </div>

                            <Divider style={{margin: '12px 0'}}/>

                            <div style={{fontSize: 12}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 6}}>
                                    <span>Периодические (факт):</span>
                                    <div style={{textAlign: 'right'}}>
                                        <strong>{selected.actualPeriodic.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selected.actualPeriodic >= sameMonthPreviousYear.periodicActual ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {sameMonthPreviousYear.periodicActual > 0 ?
                                                `${(selected.actualPeriodic - sameMonthPreviousYear.periodicActual) >= 0 ? '+' : ''}${(selected.actualPeriodic - sameMonthPreviousYear.periodicActual).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 6}}>
                                    <span>Разовые (факт):</span>
                                    <div style={{textAlign: 'right'}}>
                                        <strong>{selected.oneTime.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selected.oneTime >= sameMonthPreviousYear.oneTimeActual ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {sameMonthPreviousYear.oneTimeActual > 0 ?
                                                `${(selected.oneTime - sameMonthPreviousYear.oneTimeActual) >= 0 ? '+' : ''}${(selected.oneTime - sameMonthPreviousYear.oneTimeActual).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                    <span>Планируемые (периодические):</span>
                                    <div style={{textAlign: 'right'}}>
                                        <strong>{selected.planned.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selected.planned >= sameMonthPreviousYear.periodicPlanned ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {sameMonthPreviousYear.periodicPlanned > 0 ?
                                                `${(selected.planned - sameMonthPreviousYear.periodicPlanned) >= 0 ? '+' : ''}${(selected.planned - sameMonthPreviousYear.periodicPlanned).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card size="small">
                            <Statistic
                                title={`Фактический доход (январь-${getMonthName(selectedMonth).toLowerCase()} ${selectedYear})`}
                                value={selectedYearToDate.totalActual}
                                precision={2}
                                valueStyle={{color: '#1890ff'}}
                                prefix="€"
                                suffix={
                                    <Tag color={changes.yearly.isPositive ? 'success' : 'error'}>
                                        {changes.yearly.isPositive ? <RiseOutlined/> : <FallOutlined/>}
                                        {Math.abs(changes.yearly.percent).toFixed(1)}%
                                    </Tag>
                                }
                            />
                            <div style={{fontSize: 12, color: '#666', marginTop: 8}}>
                                vs аналогичный период {selectedYear - 1}: {changes.yearly.value.toFixed(2)}€
                            </div>

                            <Divider style={{margin: '12px 0'}}/>

                            <div style={{fontSize: 12}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 6}}>
                                    <span>Периодические (факт):</span>
                                    <div style={{textAlign: 'right'}}>
                                        <strong>{selectedYearToDate.periodicActual.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selectedYearToDate.periodicActual >= previousYearToDate.periodicActual ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {previousYearToDate.periodicActual > 0 ?
                                                `${(selectedYearToDate.periodicActual - previousYearToDate.periodicActual) >= 0 ? '+' : ''}${(selectedYearToDate.periodicActual - previousYearToDate.periodicActual).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 6}}>
                                    <span>Разовые (факт):</span>
                                    <div style={{textAlign: 'right'}}>
                                        <strong>{selectedYearToDate.oneTimeActual.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selectedYearToDate.oneTimeActual >= previousYearToDate.oneTimeActual ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {previousYearToDate.oneTimeActual > 0 ?
                                                `${(selectedYearToDate.oneTimeActual - previousYearToDate.oneTimeActual) >= 0 ? '+' : ''}${(selectedYearToDate.oneTimeActual - previousYearToDate.oneTimeActual).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>

                                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                    <span>Планируемые (периодические):</span>
                                    <div style={{textAlign: 'right'}}>
                                        <strong>{selectedYearToDate.periodicPlanned.toFixed(2)}€</strong>
                                        <div style={{
                                            color: selectedYearToDate.periodicPlanned >= previousYearToDate.periodicPlanned ? '#52c41a' : '#cf1322',
                                            fontSize: 11
                                        }}>
                                            {previousYearToDate.periodicPlanned > 0 ?
                                                `${(selectedYearToDate.periodicPlanned - previousYearToDate.periodicPlanned) >= 0 ? '+' : ''}${(selectedYearToDate.periodicPlanned - previousYearToDate.periodicPlanned).toFixed(2)}€`
                                                : '0.00€'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Divider/>

                {/* Основной график */}
                <Card
                    title={`Динамика фактических доходов: ${selectedYear} vs ${selectedYear - 1} (период с января по ${getMonthName(selectedMonth).toLowerCase()})`}
                    size="small"
                    style={{marginBottom: 24}}
                >
                    {renderChart()}
                </Card>

                {/* Дополнительная статистика */}
                <Row gutter={[16, 16]}>
                    <Col span={8}>
                        <Card size="small" title="Эффективность">
                            <div style={{display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 12}}>
                                <Tag color="blue" style={{fontSize: '14px', padding: '8px 12px'}}>
                                    Активных объектов: <strong>{selected.activeObjects}</strong>
                                </Tag>
                            </div>
                            <div style={{display: 'flex', justifyContent: 'center', gap: 12}}>
                                <Tag color="green" style={{fontSize: '14px', padding: '8px 12px'}}>
                                    Платежей в месяц: <strong>{selected.paymentsCount}</strong>
                                </Tag>
                            </div>
                        </Card>
                    </Col>

                    <Col span={8}>
                        <Card size="small" title="Выполнение плана">
                            <Statistic
                                title="С начала года"
                                value={selectedYearToDate.periodicPlanned > 0 ? (selectedYearToDate.periodicActual / selectedYearToDate.periodicPlanned * 100) : 0}
                                precision={1}
                                valueStyle={{
                                    color: (selectedYearToDate.periodicActual / selectedYearToDate.periodicPlanned * 100) >= 100 ? '#52c41a' :
                                        (selectedYearToDate.periodicActual / selectedYearToDate.periodicPlanned * 100) >= 80 ? '#faad14' : '#cf1322'
                                }}
                                suffix="% выполнения"
                            />
                            <Divider style={{margin: '12px 0'}}/>
                            <Statistic
                                title="Темп роста"
                                value={changes.yearly.percent}
                                precision={1}
                                valueStyle={{color: changes.yearly.isPositive ? '#52c41a' : '#cf1322'}}
                                suffix="%"
                            />
                        </Card>
                    </Col>

                    <Col span={8}>
                        <Card size="small" title="Средние показатели">
                            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 12}}>
                                <span>Средний чек:</span>
                                <strong>
                                    {selected.paymentsCount > 0 ? (selected.actualPeriodic / selected.paymentsCount).toFixed(2) : 0}€
                                </strong>
                            </div>
                            <Divider style={{margin: '12px 0'}}/>
                            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                <span>Средний доход/объект:</span>
                                <strong>
                                    {selected.activeObjects > 0 ? (selectedYearToDate.periodicActual / selected.activeObjects / displayMonthsCount).toFixed(2) : 0}€
                                </strong>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Пояснительная информация */}
                <Divider/>
                <div style={{fontSize: '12px', color: '#666', textAlign: 'center'}}>
                    <strong>Примечание:</strong> График отображает ФАКТИЧЕСКИЕ поступления доходов за период с января по
                    выбранный месяц.
                    Сплошные линии - выбранный год, пунктирные - предыдущий год. Синие - периодические доходы (платежи),
                    зеленые - разовые доходы.
                </div>

            </Card>
        </div>
    );
};

export default IncomeDashboard;